import { useApolloClient } from "@apollo/client";
import React from "react";
import { GET_PERSON_RELATIONSHIPS } from "src/components/graphql/queries";
import { useCreateFamily } from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { canonicalPhoneNumber } from "src/services/format";
import type { GuardianEntry, NewGuardianEntry } from "./useGuardianEntries";

export type StudentAddress = {
  street_address: string | null;
  street_address_line_2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
};

interface UseAddGuardiansToStudentParams {
  organizationId: string;
  studentId: string;
  studentAddress: StudentAddress;
}

/**
 * Links staged guardians (existing by id, or newly created) to an existing
 * student via createFamily: no student is created, each guardian gets a
 * guardian -> student relationship, and the relationships query is refreshed.
 */
export function useAddGuardiansToStudent({
  organizationId,
  studentId,
  studentAddress,
}: UseAddGuardiansToStudentParams) {
  const toast = useAvelaToast();
  const { mutateAsync: createFamily } = useCreateFamily();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const submit = async (entries: GuardianEntry[]): Promise<boolean> => {
    if (entries.length === 0) return false;
    setIsSubmitting(true);
    try {
      const guardians = entries
        .filter((entry): entry is NewGuardianEntry => entry.kind === "new")
        .map((entry) => ({
          id: entry.id,
          first_name: entry.firstName,
          middle_name: entry.middleName,
          last_name: entry.lastName,
          email_address: entry.emailAddress,
          // Normalize any entered format to E.164 before transmitting.
          phone_number: entry.phoneNumber
            ? canonicalPhoneNumber(entry.phoneNumber) || null
            : null,
          reference_id: entry.referenceId,
          ...(entry.useStudentAddress
            ? studentAddress
            : {
                street_address: entry.streetAddress,
                street_address_line_2: entry.streetAddressLine2,
                city: entry.city,
                state: entry.state,
                zip_code: entry.zipCode,
              }),
        }));
      // Guardian is first_person, the existing student is second_person.
      const relationships = entries.map((entry) => ({
        first_person: entry.id,
        second_person: studentId,
      }));

      await createFamily({
        body: {
          data: { organizationId, students: [], guardians, relationships },
        },
      });
      await apolloClient.refetchQueries({
        include: [GET_PERSON_RELATIONSHIPS],
      });
      toast({
        title: "Parent/guardian added",
        status: "success",
        isClosable: true,
      });
      return true;
    } catch (error) {
      console.error(error);
      toast.error({ title: "Error adding parent/guardian" });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submit, isSubmitting };
}
